import { FunctionComponent, RefObject, useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { faBolt, faObjectGroup, faShuffle, faTag } from "@fortawesome/pro-regular-svg-icons";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";

import { Icon } from "@/components/Icon/Icon";
import { classNames } from "@/utils/classNames";
import { ColumnConfig } from "@/pages/Private/pages/List/schema/columnConfig";
import { EventOutreachSchemaType } from "@/pages/Private/pages/List/schema/list";

import { CharCounter } from "./components/CharCounter";
import { InsertPopover } from "./components/InsertPopover";
import { useAiSnippetFooterItems } from "./hooks/useAiSnippetFooterItems";
import { useSpintaxFooterItems } from "./hooks/useSpintaxFooterItems";
import { useTemplateFooterItems } from "./hooks/useTemplateFooterItems";
import { useVariableFooterItems } from "./hooks/useVariableFooterItems";
import { FooterPopoverKind, FooterPopoverSection } from "./utils/editorFooter.types";

export interface EditorFooterProps {
	charCount: number;
	charLimit: number;
	/** Inserts a token at the editor cursor; returns whether it succeeded. */
	insertToken: (token: string) => boolean;
	/** Snapshots the editor cursor (used before async template inserts). */
	captureCursor: () => void;
	listColumns?: ColumnConfig[];
	/** The list's event-outreach row; feeds the Variable popover's Event section (PREC-4010). */
	listEvent?: EventOutreachSchemaType | null;
	listId?: number;
	currentLeadIndex?: number;
	/** Opens the spintax create modal (delegated to the Spintax panel). */
	onCreateSpintax?: () => void;
	/** Opens the AI snippet create modal (delegated to the AiSnippets panel). */
	onCreateAiSnippet?: () => void;
	showAiSnippetButton?: boolean;
}

const ICONS: Record<FooterPopoverKind, IconDefinition> = {
	variable: faTag,
	spintax: faShuffle,
	aiSnippet: faBolt,
	templates: faObjectGroup,
};

const BUTTON_ICON_CLASSES: Record<FooterPopoverKind, string> = {
	variable: "text-text-meta",
	spintax: "text-spintax",
	aiSnippet: "text-blue-action",
	templates: "text-text-meta",
};

const ITEM_ICON_WRAP_CLASSES: Record<FooterPopoverKind, string> = {
	variable: "bg-background-light text-text-meta",
	spintax: "bg-spintax-light text-spintax",
	aiSnippet: "bg-blue-action-200 text-blue-action",
	templates: "bg-background-light text-text-meta border border-border-light",
};

/**
 * Insert bar rendered below the campaign message editors: Variable / Spintax /
 * AI snippet / Templates buttons with searchable drop-up popovers, plus a
 * live character counter (mirrors the campaign-builder mockup's bottom bar).
 */
export const EditorFooter: FunctionComponent<EditorFooterProps> = ({
	charCount,
	charLimit,
	insertToken,
	captureCursor,
	listColumns,
	listEvent,
	listId,
	currentLeadIndex,
	onCreateSpintax,
	onCreateAiSnippet,
	showAiSnippetButton = true,
}) => {
	const { t } = useTranslation();

	const [openKind, setOpenKind] = useState<FooterPopoverKind | null>(null);
	const [search, setSearch] = useState<string>("");

	const variableButtonRef = useRef<HTMLButtonElement>(null);
	const spintaxButtonRef = useRef<HTMLButtonElement>(null);
	const aiSnippetButtonRef = useRef<HTMLButtonElement>(null);
	const templatesButtonRef = useRef<HTMLButtonElement>(null);

	const anchorRefs: Record<FooterPopoverKind, RefObject<HTMLButtonElement>> = {
		variable: variableButtonRef,
		spintax: spintaxButtonRef,
		aiSnippet: aiSnippetButtonRef,
		templates: templatesButtonRef,
	};

	const close = useCallback(() => {
		setOpenKind(null);
		setSearch("");
	}, []);

	const toggle = (kind: FooterPopoverKind) => {
		setSearch("");
		setOpenKind((previous) => (previous === kind ? null : kind));
	};

	const handleInsert = useCallback(
		(token: string): boolean => {
			const inserted = insertToken(token);

			close();

			return inserted;
		},
		[insertToken, close]
	);

	const handleCreateSpintax = onCreateSpintax
		? () => {
				close();
				onCreateSpintax();
			}
		: undefined;

	const handleCreateAiSnippet = onCreateAiSnippet
		? () => {
				close();
				onCreateAiSnippet();
			}
		: undefined;

	const variableSections = useVariableFooterItems({
		listColumns,
		listEvent,
		currentLeadIndex,
		search,
		onInsert: handleInsert,
	});

	const spintaxSections = useSpintaxFooterItems({
		listColumns,
		search,
		onInsert: handleInsert,
		onCreateNew: handleCreateSpintax,
	});

	const aiSnippetSections = useAiSnippetFooterItems({
		listColumns,
		listId,
		search,
		onInsert: handleInsert,
		onCreateNew: handleCreateAiSnippet,
		captureCursor,
	});

	const templateSections = useTemplateFooterItems({
		search,
		onInsert: handleInsert,
	});

	const popoverConfig: Record<
		FooterPopoverKind,
		{ sections: FooterPopoverSection[]; searchPlaceholder: string; emptyLabel: string }
	> = {
		variable: {
			sections: variableSections,
			searchPlaceholder: t("variables.section.searchPlaceholder"),
			emptyLabel: t("variables.section.emptySearch"),
		},
		spintax: {
			sections: spintaxSections,
			searchPlaceholder: t("spintax.section.searchPlaceholder"),
			emptyLabel: t("spintax.section.emptySearch"),
		},
		aiSnippet: {
			sections: aiSnippetSections,
			searchPlaceholder: t("aiSnippet.section.searchPlaceholder"),
			emptyLabel: t("aiSnippet.section.emptySearch"),
		},
		templates: {
			sections: templateSections,
			searchPlaceholder: t("editorFooter.searchTemplates"),
			emptyLabel: t("editorFooter.emptyTemplates"),
		},
	};

	const renderButton = (kind: FooterPopoverKind, label: string) => (
		<button
			ref={anchorRefs[kind]}
			className={classNames(
				"inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-white border rounded-md text-xs font-medium shadow-sm transition-colors",
				openKind === kind
					? "border-border text-text shadow"
					: "border-border-light text-text-meta hover:text-text hover:border-border"
			)}
			data-test={`editor-footer-button-${kind}`}
			type="button"
			onClick={() => toggle(kind)}
		>
			<Icon className={classNames("w-3.5 h-3.5", BUTTON_ICON_CLASSES[kind])} icon={ICONS[kind]} />
			<span>{label}</span>
		</button>
	);

	return (
		<div
			className="w-full flex flex-col gap-2 px-2.5 py-2 bg-background-light border border-t-0 border-border-light rounded-b-lg"
			data-test="editor-footer"
		>
			<div className="flex items-center gap-1.5 flex-wrap w-full">
				{renderButton("variable", t("editorFooter.variable"))}
				{renderButton("spintax", t("editorFooter.spintax"))}
				{showAiSnippetButton && renderButton("aiSnippet", t("editorFooter.aiSnippet"))}
				<div className="w-px h-[22px] bg-border-light mx-0.5 self-center" />
				{renderButton("templates", t("editorFooter.templates"))}
			</div>

			<div className="flex items-center justify-between gap-2 w-full">
				<span className="text-[11px] text-text-disabled">{t("editorFooter.typeHint")}</span>
				<CharCounter charCount={charCount} limit={charLimit} />
			</div>

			{openKind && (
				<InsertPopover
					anchorRef={anchorRefs[openKind]}
					emptyLabel={popoverConfig[openKind].emptyLabel}
					itemIcon={ICONS[openKind]}
					itemIconWrapClassName={ITEM_ICON_WRAP_CLASSES[openKind]}
					search={search}
					searchPlaceholder={popoverConfig[openKind].searchPlaceholder}
					sections={popoverConfig[openKind].sections}
					onClose={close}
					onSearchChange={setSearch}
				/>
			)}
		</div>
	);
};
